import TurndownService from 'turndown';

import React from 'react';

import { FiPrinter } from 'react-icons/fi';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';

import Information from '@/components/Information';
import IconButton from '@/components/IconButton';
import Markdown from '@/components/Markdown';

import { FhirPedidoType } from '@/features/enterprise/models/types';
import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';

import DocumentPageOrientacao from '@/features/pdf/documents/DocumentPageOrientacao';

import { clinicalDocumentPdf } from '@/features/pdf/documents/ClinicalDocumentLayout';
import {
  Container,
  Header,
  HeaderTitle,
  CollapsedContent,
  Actions,
  CollapseButton,
} from './styles';

interface BoxOrientacaoProps {
  orientacao: FhirPedidoType;
  solicitacao_obrigatoria?: boolean;
  solicitacao_obrigatoria_motivo?: string;
}

const BoxOrientacao: React.FC<BoxOrientacaoProps> = ({
  orientacao,
  solicitacao_obrigatoria,
  solicitacao_obrigatoria_motivo,
}) => {
  const contentRef = React.createRef<HTMLDivElement>();

  const { user } = useAuth();
  const [{ encounter }] = useEnterpriseContext();
  const solicitacaoObrigatoriaMotivo = React.useMemo(() => {
    return getSolicitacaoObrigatoriaMotivo(
      'orientacao',
      solicitacao_obrigatoria_motivo,
    );
  }, [solicitacao_obrigatoria_motivo]);

  const [printing, setPrinting] = React.useState(false);

  const showImprimirButton = React.useMemo(() => {
    if (user?.empresa) {
      return !!user?.empresa?.features?.find(
        feature => feature.codigo === 'IMPRIMIR_ORIENTACAO_MEDICA',
      );
    }
    return true;
  }, [user]);

  const [contentHeight, setContentHeight] = React.useState<number>(0);
  const [isOpenedDetails, setIsOpenedDetails] = React.useState(false);
  const [onHover, setOnHover] = React.useState(false);

  const hasDescription = React.useMemo(() => !!orientacao.text?.div, [
    orientacao,
  ]);

  const descriptionToMarkdown = React.useMemo(() => {
    const turndownService = new TurndownService();

    return turndownService.turndown(orientacao.text?.div || '');
  }, [orientacao]);

  const toggleDetails = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (hasDescription) {
        setIsOpenedDetails(prev => !prev);
      }
    },
    [hasDescription],
  );

  const print = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      try {
        setPrinting(true);

        await clinicalDocumentPdf(DocumentPageOrientacao, {
          user,
          encounter: encounter.data,
          resource: orientacao as fhir4.CarePlan,
        });
      } catch (error) {
        throw new Error(`Erro ao gerar PDF da orientação médica: ${error}`);
      } finally {
        setPrinting(false);
      }
    },
    [encounter.data, orientacao, user],
  );

  React.useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  if (!hasDescription) {
    return (
      <Container>
        <Header>
          <HeaderTitle>{orientacao.title}</HeaderTitle>
        </Header>

        {solicitacao_obrigatoria && (
          <Information type="warning" style={{ margin: '12px 0 0' }}>
            {solicitacaoObrigatoriaMotivo}
          </Information>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderTitle highlight={isOpenedDetails || onHover}>
          {orientacao.title}
        </HeaderTitle>

        <Actions>
          {showImprimirButton && (
            <IconButton
              tooltip="Imprimir"
              onClick={print}
              disabled={printing}
              // style={{ marginRight: 16 }}
              onMouseOver={() => setOnHover(true)}
              onMouseLeave={() => setOnHover(false)}
              icon={<FiPrinter />}
            />
          )}
          <CollapseButton collapsed={isOpenedDetails} onClick={toggleDetails} />
        </Actions>
      </Header>

      <CollapsedContent
        ref={contentRef}
        highlight={isOpenedDetails || onHover}
        style={{
          maxHeight: isOpenedDetails ? contentHeight : 16,
          maxWidth: 'calc(100% - 100px',
        }}
      >
        <span style={{ textOverflow: 'ellipsis' }}>
          <Markdown text={descriptionToMarkdown} />
        </span>
      </CollapsedContent>

      {solicitacao_obrigatoria && (
        <Information type="warning" style={{ margin: '12px 0 0' }}>
          {solicitacaoObrigatoriaMotivo}
        </Information>
      )}
    </Container>
  );
};

export default BoxOrientacao;
